import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

interface UseEntitlementsResult {
  entitlements: string[];
  hasEntitlement: (id: string) => boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

export function useEntitlements(): UseEntitlementsResult {
  const { firebaseUser } = useAuth();
  const [entitlements, setEntitlements] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchEntitlements = useCallback(
    async (forceRefresh?: boolean) => {
      if (!firebaseUser) {
        setEntitlements([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        // Use method form - User type from @firebase/auth is nested and not
        // resolvable by TypeScript; cast matches pattern in AuthVerifyCompletePage
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tokenResult = await (firebaseUser as any).getIdTokenResult(
          forceRefresh
        );
        const claims = tokenResult.claims;
        const claimsEntitlements = claims.entitlements;
        if (Array.isArray(claimsEntitlements)) {
          setEntitlements(claimsEntitlements as string[]);
        } else {
          setEntitlements([]);
        }
      } catch (error) {
        console.error('Failed to fetch entitlements from token claims:', error);
        setEntitlements([]);
      } finally {
        setIsLoading(false);
      }
    },
    [firebaseUser]
  );

  useEffect(() => {
    void fetchEntitlements();
  }, [fetchEntitlements]);

  const hasEntitlement = useCallback(
    (id: string): boolean => entitlements.includes(id),
    [entitlements]
  );

  const refresh = useCallback(async () => {
    await fetchEntitlements(true);
  }, [fetchEntitlements]);

  return {
    entitlements,
    hasEntitlement,
    isLoading,
    refresh,
  };
}
